import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Flame, Phone } from "lucide-react";

export default function Login() {
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const rawNext = params.get("next") || "/dashboard";
    // Guard against open-redirects: only allow same-origin paths starting with a single '/'.
    const next = (rawNext.startsWith("/") && !rawNext.startsWith("//")) ? rawNext : "/dashboard";
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(identifier, password);
        setLoading(false);
        if (res.ok) navigate(next);
        else setError(res.error);
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
            <div className="w-full max-w-md p-card p-8 sm:p-10" data-testid="login-card">
                <div className="flex items-center gap-2 mb-6">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                        <Flame size={18} />
                    </div>
                    <div className="font-display text-3xl" style={{ color: "#1E1B4B" }}>{t("login.h")}</div>
                </div>
                <p className="text-sm mb-6" style={{ color: "#525252" }}>
                    {t("login.sub")}
                </p>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("login.email")}</label>
                        <input
                            type="text"
                            required
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder="you@example.com or +91 98765 43210"
                            className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2"
                            style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                            data-testid="login-email-input"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("login.password")}</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 rounded-full border focus:outline-none focus:ring-2"
                            style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                            data-testid="login-password-input"
                        />
                    </div>
                    {error && <div className="text-sm" style={{ color: "#7F1D1D" }} data-testid="login-error">{error}</div>}
                    <div className="flex items-center justify-between">
                        <Link to="/forgot-password" className="text-xs underline" style={{ color: "#7F1D1D" }} data-testid="login-forgot-link">
                            {t("login.forgot")}
                        </Link>
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="login-submit-button">
                        {loading ? t("login.submitting") : t("login.submit")}
                    </button>
                </form>
                <div className="mt-6 flex flex-col gap-2 text-sm text-center" style={{ color: "#525252" }}>
                    <span>
                        {t("login.new")} <Link to="/register" className="underline" style={{ color: "#1E1B4B" }} data-testid="login-register-link">{t("login.new_link")}</Link>
                    </span>
                    <Link to="/register?mode=phone" className="inline-flex items-center justify-center gap-1.5 underline" style={{ color: "#1E1B4B" }} data-testid="login-signup-phone-link">
                        <Phone size={12} /> {t("login.signup_mobile")}
                    </Link>
                </div>
            </div>
        </div>
    );
}
